import { useEffect, useState } from 'react'
import { ErrorState, RadioGroup, Textarea } from '@/components/ui'
import { cn } from '@/lib/utils.ts'
import { customerApi } from '../lib/api'
import { AppointmentScheduler, type SchedulerSlot } from './AppointmentScheduler'

export type AppointmentDuration = 15 | 30 | 60
export type AppointmentLocation = 'remote' | 'onsite'

export interface AppointmentBookingValue {
  duration: AppointmentDuration
  location: AppointmentLocation
  slot: SchedulerSlot | null
  note: string
}

export interface AppointmentBookingErrors {
  slot?: string
  note?: string
}

interface AvailabilityDebug {
  route?: string
  calls?: Array<{
    endpoint: string
    method?: string
    params?: Record<string, unknown>
    status?: number
    error?: string
    responseSummary?: unknown
  }>
  resolution?: Record<string, unknown>
}

interface AvailabilityResponse {
  ticketId: string | null
  assignee: { id: number; name: string | null; kind?: 'employee' | 'department' } | null
  slots: SchedulerSlot[]
  note: string
  hasSchedule?: boolean
  busyCount?: number
  debug?: AvailabilityDebug
}

interface AppointmentBookingFormProps {
  /** Ticket ID for which availability is loaded. `null` → do not load slots. */
  ticketId: string | null
  value: AppointmentBookingValue
  onChange: (next: AppointmentBookingValue) => void
  errors?: AppointmentBookingErrors
  /** Mark note as required and show placeholder. */
  noteRequired?: boolean
  /** Show debug panel (default: true). */
  showDebug?: boolean
  /** Show onsite hint when onsite location is selected (default: true). */
  showOnsiteHint?: boolean
}

export const DEFAULT_APPOINTMENT_VALUE: AppointmentBookingValue = {
  duration: 30,
  location: 'remote',
  slot: null,
  note: '',
}

/**
 * Appointment booking form: duration + slot picker + location + note.
 *
 * Fetches availability from the backend automatically whenever the ticket,
 * duration, or location changes. Used identically in the modal and on the
 * full booking page — a single piece of code for both places.
 */
export function AppointmentBookingForm({
  ticketId,
  value,
  onChange,
  errors,
  noteRequired = false,
  showDebug = true,
  showOnsiteHint = true,
}: AppointmentBookingFormProps) {
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reload slots whenever ticket/duration/location changes. Slot is reset
  // because the previous selection may no longer be valid in the new slot list.
  useEffect(() => {
    if (!ticketId) {
      setAvailability(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    if (value.slot != null) {
      onChange({ ...value, slot: null })
    }
    customerApi
      .get<AvailabilityResponse>(`/tickets/${ticketId}/availability`, {
        params: { duration: value.duration, location: value.location },
      })
      .then((r) => {
        if (cancelled) return
        setAvailability(r.data)
      })
      .catch((e) => {
        if (cancelled) return
        const ax = e as { response?: { data?: { error?: string } }; message?: string }
        setError(ax.response?.data?.error ?? ax.message ?? 'Slots konnten nicht geladen werden')
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [ticketId, value.duration, value.location])

  const update = (patch: Partial<AppointmentBookingValue>) => onChange({ ...value, ...patch })

  return (
    <div className="space-y-3">
      <DurationPicker
        value={value.duration}
        onChange={(d) => update({ duration: d })}
      />

      {!ticketId ? (
        <div className="text-body text-gray-500">
          Bitte erst ein Ticket auswählen, um die Verfügbarkeit des zugewiesenen Technikers zu sehen.
        </div>
      ) : error ? (
        <ErrorState size="sm" description={error} />
      ) : (
        <>
          <AppointmentScheduler
            slots={availability?.slots ?? []}
            selectedSlotStart={value.slot?.start ?? null}
            onSelect={(s) => update({ slot: s })}
            technicianName={availability?.assignee?.name ?? null}
            assigneeKind={availability?.assignee?.kind ?? null}
            note={availability?.note ?? null}
            loading={loading}
            error={errors?.slot}
          />
          {showDebug && availability?.debug ? (
            <AvailabilityDebugPanel debug={availability.debug} />
          ) : null}
        </>
      )}

      <RadioGroup<AppointmentLocation>
        label="Ort"
        value={value.location}
        onValueChange={(l) => update({ location: l })}
        options={[
          { value: 'remote', label: 'Remote' },
          { value: 'onsite', label: 'Vor Ort' },
        ]}
      />

      <Textarea
        label={noteRequired ? 'Anmerkung' : 'Anmerkung (optional)'}
        rows={3}
        value={value.note}
        onChange={(e) => update({ note: e.target.value })}
        error={errors?.note}
        required={noteRequired}
        placeholder={noteRequired ? 'Bitte gib alles an, was bei der Vorbereitung helfen könnte.' : undefined}
      />

      {showOnsiteHint && value.location === 'onsite' ? (
        <div className="text-caption text-gray-500">
          Vor-Ort-Termine werden als Anfrage gesendet und vom Techniker
          final bestätigt (Anfahrt wird eingeplant).
        </div>
      ) : null}
    </div>
  )
}

/**
 * Converts a selected slot into the POST payload for
 * `POST /tickets/:id/appointment`.
 */
export function slotToAppointmentPayload(
  slot: SchedulerSlot,
  location: AppointmentLocation,
  note: string,
) {
  const start = new Date(slot.start)
  const end = new Date(slot.end)
  const date = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`
  const time = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`
  const duration = Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000))
  return {
    date,
    time,
    duration,
    location,
    note: note.trim() || undefined,
  }
}

// ─── Dauer-Auswahl ─────────────────────────────────────────────────────────

function DurationPicker({
  value,
  onChange,
}: {
  value: AppointmentDuration
  onChange: (v: AppointmentDuration) => void
}) {
  const options: Array<{ value: AppointmentDuration; label: string }> = [
    { value: 15, label: '15 min' },
    { value: 30, label: '30 min' },
    { value: 60, label: '1 Stunde' },
  ]
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Dauer</label>
      <div className="inline-flex rounded-md border border-gray-300 bg-white overflow-hidden">
        {options.map((o, i) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={cn(
              'px-4 py-1.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              value === o.value
                ? 'bg-brand-700 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50',
              i > 0 && 'border-l border-gray-300',
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Debug-Panel ───────────────────────────────────────────────────────────

function AvailabilityDebugPanel({ debug }: { debug: AvailabilityDebug }) {
  return (
    <details className="mt-3 rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-caption text-gray-700">
      <summary className="cursor-pointer select-none font-medium text-gray-800">
        Debug-Infos (TANSS-Routen &amp; Antworten)
      </summary>
      <div className="mt-2 space-y-3">
        {debug.route ? (
          <div>
            <div className="font-mono text-gray-500">Route: {debug.route}</div>
          </div>
        ) : null}
        {debug.calls && debug.calls.length > 0 ? (
          <div>
            <div className="font-medium mb-1">TANSS-Aufrufe</div>
            <ul className="space-y-2">
              {debug.calls.map((c, i) => (
                <li key={i} className="rounded bg-white border border-gray-200 p-2">
                  <div className="font-mono">
                    <span className="text-brand-700">{c.method ?? 'GET'}</span>{' '}
                    {c.endpoint}
                    {c.params ? (
                      <span className="text-gray-500">?{new URLSearchParams(Object.entries(c.params).map(([k, v]) => [k, String(v)])).toString()}</span>
                    ) : null}
                    {c.status != null ? (
                      <span
                        className={
                          c.status >= 200 && c.status < 300
                            ? 'ml-2 text-green-700'
                            : 'ml-2 text-red-700'
                        }
                      >
                        → {c.status}
                      </span>
                    ) : null}
                    {c.error ? <span className="ml-2 text-red-700">✗ {c.error}</span> : null}
                  </div>
                  {c.responseSummary != null ? (
                    <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-caption text-gray-700">
                      {JSON.stringify(c.responseSummary, null, 2)}
                    </pre>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {debug.resolution ? (
          <div>
            <div className="font-medium mb-1">Auflösung (Techniker-Erkennung)</div>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded bg-white border border-gray-200 p-2 font-mono text-caption text-gray-700">
              {JSON.stringify(debug.resolution, null, 2)}
            </pre>
          </div>
        ) : null}
      </div>
    </details>
  )
}
